import { useState, useEffect } from "react";
import { Cog, Plus, Trash2, Play, AlertCircle, CheckCircle2 } from "lucide-react";
import { z } from "zod";
import type { Recipe, RecipeIngredient, BatchRun } from "../types";
import { readRecipes, writeRecipes, runBatch, currentMonth, grandTotal } from "../services/inventory";
import UnitCombobox from "../components/UnitCombobox";

type Tab = "recipes" | "run";

const recipeSchema = z.object({
  name: z.string().min(1, "Recipe name is required"),
  finishesGood: z.string().min(1, "Finished good name is required"),
  unit: z.string().min(1, "Unit is required"),
});

const batchSchema = z.object({
  batchQty: z.number().positive("Batch quantity must be positive"),
  unitPrice: z.number().min(0, "Price cannot be negative"),
});

export default function Manufacturing() {
  const [tab, setTab] = useState<Tab>("recipes");
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Recipe form state
  const [showForm, setShowForm] = useState(false);
  const [rName, setRName] = useState("");
  const [rGood, setRGood] = useState("");
  const [rUnit, setRUnit] = useState("");
  const [rIngs, setRIngs] = useState<RecipeIngredient[]>([]);
  const [rErrors, setRErrors] = useState<string[]>([]);

  // Ingredient form fields (for the add-ingredient row)
  const [ingItem, setIngItem] = useState("");
  const [ingQty, setIngQty] = useState("1");
  const [ingUnit, setIngUnit] = useState("");
  const [ingSection, setIngSection] = useState<"raw_materials" | "packaging">("raw_materials");

  // Batch run state
  const [selRecipe, setSelRecipe] = useState("");
  const [batchQty, setBatchQty] = useState("1");
  const [wastePct, setWastePct] = useState("0");
  const [unitPrice, setUnitPrice] = useState("");
  const [running, setRunning] = useState(false);

  useEffect(() => {
    readRecipes().then(setRecipes);
  }, []);

  const save = async (updated: Recipe[]) => {
    setRecipes(updated);
    await writeRecipes(updated);
  };

  const addIngredient = () => {
    if (!ingItem.trim() || !parseFloat(ingQty) || !ingUnit.trim()) return;
    setRIngs([...rIngs, { item: ingItem.trim(), quantity: parseFloat(ingQty), unit: ingUnit.trim(), section: ingSection }]);
    setIngItem("");
    setIngQty("1");
    setIngUnit("");
  };

  const removeIngredient = (idx: number) => {
    setRIngs(rIngs.filter((_, i) => i !== idx));
  };

  const saveRecipe = async () => {
    setRErrors([]);
    const parsed = recipeSchema.safeParse({ name: rName, finishesGood: rGood, unit: rUnit });
    if (!parsed.success) {
      setRErrors(parsed.error.errors.map((e) => e.message));
      return;
    }
    if (rIngs.length === 0) {
      setRErrors(["At least one ingredient is required"]);
      return;
    }
    const recipe: Recipe = { name: rName.trim(), finishesGood: rGood.trim(), unit: rUnit.trim(), ingredients: rIngs };
    await save([...recipes, recipe]);
    setShowForm(false);
    setRName("");
    setRGood("");
    setRUnit("");
    setRIngs([]);
    setSuccess(`Recipe "${recipe.name}" saved`);
    setTimeout(() => setSuccess(null), 3000);
  };

  const deleteRecipe = async (idx: number) => {
    const updated = recipes.filter((_, i) => i !== idx);
    await save(updated);
  };

  const runProduction = async () => {
    setError(null);
    setSuccess(null);
    const recipe = recipes.find((r) => r.name === selRecipe);
    if (!recipe) { setError("Select a recipe"); return; }

    const parsed = batchSchema.safeParse({ batchQty: parseFloat(batchQty) || 0, unitPrice: parseFloat(unitPrice) || 0 });
    if (!parsed.success) {
      setError(parsed.error.errors.map((e) => e.message).join(", "));
      return;
    }

    setRunning(true);
    try {
      const month = currentMonth();
      const run: BatchRun = { recipeName: selRecipe, batchQty: parsed.data.batchQty, wastePct: parseFloat(wastePct) || 0, unitPrice: parsed.data.unitPrice };
      await runBatch(month, recipe, run);
      setSuccess(`Batch complete: ${run.batchQty} × ${recipe.finishesGood} added to ${month}`);
      setBatchQty("1");
      setWastePct("0");
      setUnitPrice("");
    } catch (e: any) {
      setError(e?.message || "Batch failed");
    } finally {
      setRunning(false);
    }
  };

  // Preview calculations for selected recipe
  const previewRecipe = recipes.find((r) => r.name === selRecipe);
  const previewQty = parseFloat(batchQty) || 0;
  const previewWaste = parseFloat(wastePct) || 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-citilux-blue-600/20 flex items-center justify-center">
          <Cog className="w-5 h-5 text-citilux-blue-400" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Manufacturing</h1>
          <p className="text-sm text-gray-500 dark:text-metallic-silver/60">Recipes & Production Runs</p>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="flex items-center gap-2 bg-red-100 dark:bg-red-500/10 border border-red-300 dark:border-red-500/20 text-red-700 dark:text-red-400 text-sm rounded-xl px-4 py-3">
          <AlertCircle className="w-4 h-4 shrink-0" /> {error}
          <button onClick={() => setError(null)} className="ml-auto text-red-500/60 hover:text-red-700 dark:hover:text-red-400">✕</button>
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 bg-green-100 dark:bg-green-500/10 border border-green-300 dark:border-green-500/20 text-green-700 dark:text-green-400 text-sm rounded-xl px-4 py-3">
          <CheckCircle2 className="w-4 h-4 shrink-0" /> {success}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 dark:bg-navy-800 rounded-xl p-1 w-fit">
        <button onClick={() => setTab("recipes")} className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${tab === "recipes" ? "bg-white dark:bg-navy-700 text-gray-900 dark:text-white shadow-sm" : "text-gray-500 dark:text-metallic-silver hover:text-gray-700 dark:hover:text-white"}`}>
          Recipes
        </button>
        <button onClick={() => setTab("run")} className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${tab === "run" ? "bg-white dark:bg-navy-700 text-gray-900 dark:text-white shadow-sm" : "text-gray-500 dark:text-metallic-silver hover:text-gray-700 dark:hover:text-white"}`}>
          Production Run
        </button>
      </div>

      {/* ──────────────── Recipes Tab ──────────────── */}
      {tab === "recipes" && (
        <div className="space-y-4">
          {!showForm && (
            <button onClick={() => setShowForm(true)} className="flex items-center gap-2 px-4 py-2.5 bg-citilux-blue-600 hover:bg-citilux-blue-500 text-sm font-semibold text-white rounded-xl transition-colors">
              <Plus className="w-4 h-4" /> New Recipe
            </button>
          )}

          {/* Add recipe form */}
          {showForm && (
            <div className="bg-white dark:bg-navy-800/40 border border-light-border dark:border-navy-700/40 rounded-2xl p-5 space-y-4">
              <h3 className="text-sm font-semibold text-gray-800 dark:text-white/80">New Recipe</h3>

              {rErrors.length > 0 && (
                <div className="flex flex-col gap-1 bg-amber-100 dark:bg-amber-500/10 border border-amber-300 dark:border-amber-500/20 text-amber-700 dark:text-amber-400 text-sm rounded-xl px-4 py-3">
                  {rErrors.map((e, i) => (<span key={i} className="flex items-center gap-2"><AlertCircle className="w-4 h-4 shrink-0" />{e}</span>))}
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <input value={rName} onChange={(e) => setRName(e.target.value)} placeholder="Recipe name" className="bg-gray-50 dark:bg-navy-900 border border-light-border dark:border-navy-700 rounded-xl px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:border-citilux-blue-500 placeholder:text-gray-400" />
                <input value={rGood} onChange={(e) => setRGood(e.target.value)} placeholder="Finished good name" className="bg-gray-50 dark:bg-navy-900 border border-light-border dark:border-navy-700 rounded-xl px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:border-citilux-blue-500 placeholder:text-gray-400" />
                <UnitCombobox value={rUnit} onChange={setRUnit} placeholder="Unit (e.g. 20L Buckets)" />
              </div>

              {/* Ingredients */}
              <div>
                <h4 className="text-xs uppercase tracking-widest text-gray-500 dark:text-metallic-silver/60 mb-2">Ingredients</h4>
                {rIngs.length > 0 && (
                  <div className="space-y-1 mb-3">
                    {rIngs.map((ing, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm text-gray-700 dark:text-white/80 bg-gray-50 dark:bg-navy-900 rounded-lg px-3 py-1.5">
                        <span className="font-medium">{ing.item}</span>
                        <span className="text-gray-400">×</span>
                        <span>{ing.quantity}</span>
                        <span className="text-gray-400">{ing.unit}</span>
                        <span className="text-[10px] uppercase text-gray-400 dark:text-metallic-silver/50">({ing.section === "raw_materials" ? "RM" : "PKG"})</span>
                        <button onClick={() => removeIngredient(idx)} className="ml-auto text-gray-400 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                  <input value={ingItem} onChange={(e) => setIngItem(e.target.value)} placeholder="Item" className="bg-gray-50 dark:bg-navy-900 border border-light-border dark:border-navy-700 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:border-citilux-blue-500 placeholder:text-gray-400" />
                  <input type="number" step="1" min="0" value={ingQty} onChange={(e) => setIngQty(e.target.value)} placeholder="Qty" className="bg-gray-50 dark:bg-navy-900 border border-light-border dark:border-navy-700 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:border-citilux-blue-500" />
                  <UnitCombobox value={ingUnit} onChange={setIngUnit} placeholder="Unit" />
                  <select value={ingSection} onChange={(e) => setIngSection(e.target.value as "raw_materials" | "packaging")} className="bg-gray-50 dark:bg-navy-900 border border-light-border dark:border-navy-700 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:border-citilux-blue-500">
                    <option value="raw_materials">Raw Materials</option>
                    <option value="packaging">Packaging</option>
                  </select>
                  <button onClick={addIngredient} className="flex items-center justify-center gap-1 bg-gray-200 dark:bg-navy-700 hover:bg-gray-300 dark:hover:bg-navy-600 text-sm font-medium rounded-lg px-3 py-2 transition-colors"><Plus className="w-3.5 h-3.5" /> Add</button>
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                <button onClick={saveRecipe} className="bg-citilux-blue-600 hover:bg-citilux-blue-500 text-sm font-semibold text-white rounded-xl px-5 py-2.5 transition-colors">Save Recipe</button>
                <button onClick={() => setShowForm(false)} className="px-4 py-2.5 rounded-xl border border-light-border dark:border-navy-700 text-sm text-gray-600 dark:text-metallic-silver hover:bg-gray-100 dark:hover:bg-navy-700 transition-colors">Cancel</button>
              </div>
            </div>
          )}

          {/* Recipe list */}
          {recipes.length === 0 && !showForm && (
            <div className="text-center py-16 text-gray-400 dark:text-metallic-silver/50 text-sm">No recipes yet. Create one above.</div>
          )}
          {recipes.map((recipe, idx) => (
            <div key={idx} className="bg-white dark:bg-navy-800/40 border border-light-border dark:border-navy-700/40 rounded-2xl p-5 space-y-3">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white">{recipe.name}</h3>
                  <p className="text-sm text-gray-500 dark:text-metallic-silver/70">
                    Produces: <span className="font-medium text-gray-700 dark:text-white/80">{recipe.finishesGood}</span>
                    {recipe.unit && <span className="text-gray-400"> ({recipe.unit})</span>}
                  </p>
                </div>
                <button onClick={() => deleteRecipe(idx)} className="text-gray-400 dark:text-metallic-silver/50 hover:text-red-600 dark:hover:text-red-400 p-1"><Trash2 className="w-4 h-4" /></button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                {(["raw_materials", "packaging"] as const).map((section) => {
                  const items = recipe.ingredients.filter((i) => i.section === section);
                  if (items.length === 0) return null;
                  return (
                    <div key={section}>
                      <h4 className="text-xs uppercase tracking-widest text-gray-500 dark:text-metallic-silver/60 mb-1">
                        {section === "raw_materials" ? "Raw Materials" : "Packaging"}
                      </h4>
                      <ul className="space-y-0.5">
                        {items.map((ing, i) => (
                          <li key={i} className="text-gray-700 dark:text-white/70">
                            {ing.item} — {ing.quantity} {ing.unit}
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ──────────────── Production Run Tab ──────────────── */}
      {tab === "run" && (
        <div className="max-w-xl space-y-4">
          <div className="bg-white dark:bg-navy-800/40 border border-light-border dark:border-navy-700/40 rounded-2xl p-5 space-y-4">
            <h3 className="text-sm font-semibold text-gray-800 dark:text-white/80">Run Production Batch</h3>

            <div className="space-y-3">
              <div>
                <label className="block text-xs uppercase tracking-widest text-gray-500 dark:text-metallic-silver/60 mb-1">Recipe</label>
                <select value={selRecipe} onChange={(e) => setSelRecipe(e.target.value)} className="w-full bg-gray-50 dark:bg-navy-900 border border-light-border dark:border-navy-700 rounded-xl px-3 py-2.5 text-sm text-gray-900 dark:text-white focus:outline-none focus:border-citilux-blue-500">
                  <option value="">— Select —</option>
                  {recipes.map((r, idx) => (<option key={idx} value={r.name}>{r.name}</option>))}
                </select>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs uppercase tracking-widest text-gray-500 dark:text-metallic-silver/60 mb-1">Batch Qty</label>
                  <input type="number" step="1" min="1" value={batchQty} onChange={(e) => setBatchQty(e.target.value)} className="w-full bg-gray-50 dark:bg-navy-900 border border-light-border dark:border-navy-700 rounded-xl px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:border-citilux-blue-500" />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-gray-500 dark:text-metallic-silver/60 mb-1">Waste/Spillage %</label>
                  <input type="number" step="0.1" min="0" value={wastePct} onChange={(e) => setWastePct(e.target.value)} className="w-full bg-gray-50 dark:bg-navy-900 border border-light-border dark:border-navy-700 rounded-xl px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:border-citilux-blue-500" />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-gray-500 dark:text-metallic-silver/60 mb-1">Sell Price (₦)</label>
                  <input type="number" step="0.01" min="0" value={unitPrice} onChange={(e) => setUnitPrice(e.target.value)} className="w-full bg-gray-50 dark:bg-navy-900 border border-light-border dark:border-navy-700 rounded-xl px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:border-citilux-blue-500" />
                </div>
              </div>

              {/* Preview */}
              {previewRecipe && previewQty > 0 && (
                <div className="bg-gray-50 dark:bg-navy-900 rounded-xl p-4 space-y-2 text-sm">
                  <h4 className="text-xs uppercase tracking-widest text-gray-500 dark:text-metallic-silver/60 mb-2">Batch Preview</h4>
                  {previewRecipe.ingredients.map((ing, i) => {
                    const base = ing.quantity * previewQty;
                    const waste = base * (previewWaste / 100);
                    return (
                      <div key={i} className="flex justify-between text-gray-700 dark:text-white/70">
                        <span>{ing.item}</span>
                        <span className="tabular-nums">-{base + waste} {ing.unit} {waste > 0 && <span className="text-amber-500">(+{waste.toFixed(1)} waste)</span>}</span>
                      </div>
                    );
                  })}
                  <div className="flex justify-between font-semibold text-gray-900 dark:text-white pt-2 border-t border-light-border dark:border-navy-700/60">
                    <span>→ {previewRecipe.finishesGood}</span>
                    <span>+{previewQty} {previewRecipe.unit}</span>
                  </div>
                </div>
              )}

              <button onClick={runProduction} disabled={running || !selRecipe} className="flex items-center justify-center gap-2 w-full bg-green-600 hover:bg-green-500 disabled:bg-gray-400 dark:disabled:bg-navy-700 text-sm font-semibold text-white rounded-xl px-5 py-3 transition-colors">
                {running ? "Processing..." : <><Play className="w-4 h-4" /> Complete Batch</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
